import React, { createContext, useState, useContext, useEffect } from 'react';
import { cartAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total_amount: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      restorePendingAndFetch();
    }
  }, [isAuthenticated]);

  // Save a cart selection made while logged out, so we can restore it after auth.
  const savePendingCartItem = (planId, config) => {
    try {
      const pending = JSON.parse(localStorage.getItem('pending_cart_items') || '[]');
      pending.push({ planId, config });
      localStorage.setItem('pending_cart_items', JSON.stringify(pending));
    } catch (error) {
      logger.error('Failed to save pending cart item:', error);
    }
  };

  // After login/signup, push any items saved while logged out, then load the cart.
  const restorePendingAndFetch = async () => {
    let restored = false;
    try {
      const pending = JSON.parse(localStorage.getItem('pending_cart_items') || '[]');
      if (pending.length > 0) {
        for (const item of pending) {
          try {
            await cartAPI.addToCart({ plan_id: item.planId, config: item.config });
            restored = true;
          } catch (error) {
            logger.error('Failed to restore pending cart item:', error);
          }
        }
        localStorage.removeItem('pending_cart_items');
      }
    } catch (error) {
      logger.error('Failed to read pending cart items:', error);
    }
    await fetchCart();
    if (restored) {
      toast.success('Your cart has been restored');
    }
  };

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await cartAPI.getCart();
      setCart(response.data);
    } catch (error) {
      logger.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (planId, config) => {
    try {
      const response = await cartAPI.addToCart({ plan_id: planId, config });
      setCart(response.data.cart);
      toast.success('Plan added to cart!');
      return true;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add to cart';
      toast.error(message);
      return false;
    }
  };

  const removeFromCart = async (planId) => {
    try {
      const response = await cartAPI.removeFromCart(planId);
      setCart(response.data.cart);
      toast.success('Plan removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await cartAPI.clearCart();
      setCart({ items: [], total_amount: 0 });
      toast.success('Cart cleared');
    } catch (error) {
      toast.error('Failed to clear cart');
    }
  };

  const getItemCount = () => {
    return cart?.items?.length || 0;
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, removeFromCart, clearCart, fetchCart, getItemCount, savePendingCartItem }}>
      {children}
    </CartContext.Provider>
  );
};